import CloseIcon from '@mui/icons-material/Close';
import { MenuList, Paper, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import pino from 'pino';
import { memo, useCallback, useEffect } from 'react';
import { useMedia } from 'react-use';

import { sideMenuStrings } from '../../../../constants/strings';
import { selectedTeamIdQuery, teamsQuery, useGetCurrentTeamQuery } from '../../../../queries/teamQuery';
import { userQuery, useSaveUserSettingQuery } from '../../../../queries/userQuery';
import { isCompanyOrSupportAdmin } from '../../../../selectors/userSelectors';
import { useZustand } from '../../../../zustandStore';

import TeamSwitcherMenu from './teamSwitcherMenu';

import type { GetTeamsResponse } from '../../../../queries/teamQuery';
import type { FC, HTMLAttributes, PropsWithChildren } from 'react';

import './index.less';

const logger = pino();

type SideMenuTeamSwitcherProps = HTMLAttributes<HTMLDivElement>;

const SideMenuTeamSwitcher: FC<PropsWithChildren<SideMenuTeamSwitcherProps>> = () => {
	const { data: teams = [] } = useQuery(teamsQuery({ state: 'active' }));
	const { data: teamId } = useQuery(selectedTeamIdQuery('selectedTeamID'));
	const { data: currentTeam } = useGetCurrentTeamQuery(teams, teamId);
	const saveUserSetting = useSaveUserSettingQuery();
	const { data: userData } = useQuery(userQuery);
	const isTeamSwitcherAsideOpen = useZustand(state => state.sideMenu.isTeamSwitcherAsideOpen);
	const { openSideMenuOnDesktopView, setIsOpenTeamSwitcherAside, setPageLoading, setPreventShowErrorPage } =
		useZustand();
	const isMobileWidth = useMedia('(max-width: 650px)');
	const selectedTeam = Array.isArray(teams) && teams.length > 0 && teams.find(team => team.id === teamId?.[0] || '');

	const closeAside = useCallback(() => {
		setIsOpenTeamSwitcherAside(false);
		if (!isMobileWidth) {
			openSideMenuOnDesktopView(false);
		}
	}, [isMobileWidth, openSideMenuOnDesktopView, setIsOpenTeamSwitcherAside]);

	useEffect(() => {
		if (!currentTeam) {
			return;
		}
		if (Array.isArray(teamId) && teamId.length === 0 && currentTeam?.id) {
			saveUserSetting.mutate({ selectedTeamID: currentTeam.id });
		}
	}, [currentTeam, teamId]);

	useEffect(() => {
		if (isTeamSwitcherAsideOpen && !isMobileWidth) {
			openSideMenuOnDesktopView(false);
		}
	}, [isMobileWidth, isTeamSwitcherAsideOpen, openSideMenuOnDesktopView]);

	const selectTeam = (teamData: GetTeamsResponse) => {
		closeAside();
		setPreventShowErrorPage(true);
		setPageLoading(true);
		saveUserSetting.mutate(
			{ selectedTeamID: teamData.id },
			{
				onError: error => {
					logger.error({ message: 'Error while saving user setting', error });
				},
				onSettled: () => {
					// we need full page refresh here to get new values from the server
					// eslint-disable-next-line react-compiler/react-compiler
					globalThis.location.href = isCompanyOrSupportAdmin(userData?.roles || []) ? '/wallet' : '/wallet/my-cards';
				},
			},
		);
	};

	return (
		<Paper
			data-testid="sideMenu-drawer"
			ts-variant="sideMenuTeamSwitcher"
			className={isTeamSwitcherAsideOpen ? 'open' : 'closed'}
		>
			<Typography component="header" ts-variant="teamSwitcherHeaderTitle">
				{sideMenuStrings.SIDE_MENU_TEAM_SWITCHER_OPTION}
			</Typography>
			<CloseIcon ts-variant="teamSwitcherCloseIcon" onClick={() => closeAside()} />
			<MenuList className="side-menu-team-switcher__menu">
				{selectedTeam && teams && teams.length > 0
					? teams.map(team => (
							<TeamSwitcherMenu
								key={team.id}
								onClick={() => selectTeam(team)}
								team={{ ...team, selected: team.id === selectedTeam.id }}
							/>
						))
					: null}
			</MenuList>
		</Paper>
	);
};

export default memo(SideMenuTeamSwitcher);
